import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CurriculumProvider } from "@/context/CurriculumContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ClassView from "@/pages/ClassView";
import StudentProfile from "@/pages/StudentProfile";
import Assess from "@/pages/Assess";
import Report from "@/pages/Report";
import ParentReport from "@/pages/ParentReport";
import OverviewMap from "@/pages/OverviewMap";
import TargetSheet from "@/pages/TargetSheet";
import SupportDoc from "@/pages/SupportDoc";
import CosPlan from "@/pages/CosPlan";
import Continuum from "@/pages/Continuum";
import CosDocument from "@/pages/CosDocument";
import ProvisionMap from "@/pages/ProvisionMap";
import SelfEval from "@/pages/SelfEval";
import ClassSummary from "@/pages/ClassSummary";
import ClassGaps from "@/pages/ClassGaps";
import Planning from "@/pages/Planning";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import Meetings from "@/pages/Meetings";
import MeetingDetail from "@/pages/MeetingDetail";
import ParentSign from "@/pages/ParentSign";
import Layout from "@/components/Layout";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        <div className="animate-pulse font-['Outfit'] text-lg">Loading…</div>
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

const protect = (el) => <Protected>{el}</Protected>;
const shell = (el) => <Protected><Layout>{el}</Layout></Protected>;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/p/:token" element={<ParentSign />} />
      <Route path="/" element={shell(<Dashboard />)} />
      <Route path="/groups" element={shell(<Groups />)} />
      <Route path="/group/:groupId" element={shell(<GroupDetail />)} />
      <Route path="/class/:classId" element={shell(<ClassView />)} />
      <Route path="/class/:classId/summary" element={protect(<ClassSummary />)} />
      <Route path="/class/:classId/planning" element={protect(<Planning />)} />
      <Route path="/class/:classId/gaps" element={protect(<ClassGaps />)} />
      <Route path="/student/:studentId" element={shell(<StudentProfile />)} />
      <Route path="/student/:studentId/assess/:subject" element={shell(<Assess />)} />
      <Route path="/student/:studentId/report" element={protect(<Report />)} />
      <Route path="/student/:studentId/parent-report" element={protect(<ParentReport />)} />
      <Route path="/class/:classId/map" element={protect(<OverviewMap />)} />
      <Route path="/group/:groupId/map" element={protect(<OverviewMap />)} />
      <Route path="/student/:studentId/targets-sheet" element={protect(<TargetSheet />)} />
      <Route path="/student/:studentId/support-plan" element={protect(<SupportDoc docType="plan" />)} />
      <Route path="/student/:studentId/support-review" element={protect(<SupportDoc docType="review" />)} />
      <Route path="/student/:studentId/cos" element={protect(<CosPlan />)} />
      <Route path="/student/:studentId/cos/document/:docType" element={protect(<CosDocument />)} />
      <Route path="/class/:classId/continuum" element={protect(<Continuum />)} />
      <Route path="/provision-map" element={protect(<ProvisionMap />)} />
      <Route path="/student/:studentId/self-eval" element={protect(<SelfEval />)} />
      <Route path="/student/:studentId/self-eval/:evalId" element={protect(<SelfEval />)} />
      <Route path="/meetings" element={shell(<Meetings />)} />
      <Route path="/meetings/:meetingId" element={shell(<MeetingDetail />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CurriculumProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </CurriculumProvider>
    </AuthProvider>
  );
}
